import React, { useEffect, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Grid, IconButton, Stack, Typography,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
      <Box sx={{ color: "text.secondary", mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

function LeaveBar({ label, used = 0, total = 0, color }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          <b style={{ color }}>{remaining}</b> / {total}
        </Typography>
      </Stack>
      <Box sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, transition: "width 0.4s ease" }} />
      </Box>
    </Box>
  );
}

export default function UserProfileNew() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/employees/logineduser/myprofile");
      setProfile(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("profilePicture", file);
      const res = await api.put("/api/employees/myprofile/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, profilePicture: res.data.profilePicture }));
      toast.success("Photo updated");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.put("/api/self-service/me", { profilePicture: null });
      setProfile((p) => ({ ...p, profilePicture: null }));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setUploading(false); }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={3}>
        <PageHeader title="My Profile" icon={<PersonOutlineOutlinedIcon />} />
        <SectionCard>
          <Typography color="text.secondary" textAlign="center" py={6}>
            Unable to load profile. Please try again.
          </Typography>
        </SectionCard>
      </Box>
    );
  }

  const leaves = profile.remainingLeave || {};
  const leaveTotals = {
    annualLeave: profile.annualLeave || 18,
    sickLeave: profile.sickLeave || 10,
    casualleave: profile.casualleave || 12,
  };

  return (
    <Box p={3}>
      <PageHeader
        title="My Profile"
        subtitle="Your personal and work information"
        icon={<PersonOutlineOutlinedIcon />}
      />

      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 4,
          background: "linear-gradient(135deg, #6C50BF 0%, #8568D4 60%, #A684E8 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", right: -60, top: -60, width: 220, height: 220, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ position: "absolute", right: 40, bottom: -80, width: 180, height: 180, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }} sx={{ position: "relative" }}>
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={profile.profilePicture}
              sx={{
                width: 96, height: 96, fontSize: 36, fontWeight: 700,
                border: "4px solid rgba(255,255,255,0.4)", bgcolor: "#fff", color: "primary.main",
              }}
            >
              {(profile.name || "U").slice(0, 1).toUpperCase()}
            </Avatar>
            <label htmlFor="avatar-upload">
              <input id="avatar-upload" type="file" accept="image/*" onChange={uploadAvatar} style={{ display: "none" }} />
              <IconButton
                component="span"
                disabled={uploading}
                sx={{
                  position: "absolute", bottom: -4, right: -4,
                  bgcolor: "#fff", color: "primary.main", border: "2px solid #fff",
                  width: 32, height: 32, "&:hover": { bgcolor: "grey.100" },
                }}
              >
                {uploading ? <CircularProgress size={16} /> : <CameraAltOutlinedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </label>
            {profile.profilePicture && (
              <IconButton
                onClick={removeAvatar}
                disabled={uploading}
                size="small"
                title="Remove profile photo"
                sx={{
                  position: "absolute", top: -4, right: -4,
                  bgcolor: "#fff", color: "error.main", border: "2px solid #fff",
                  width: 24, height: 24, "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 0.5 }}>{profile.name}</Typography>
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
              <Chip label={profile.employeeid} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600 }} />
              <Chip label={profile.role} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, textTransform: "capitalize" }} />
              {profile.department && <Chip label={profile.department} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600 }} />}
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {profile.email && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EmailOutlinedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                  <Typography sx={{ fontSize: 13, opacity: 0.95 }}>{profile.email}</Typography>
                </Stack>
              )}
              {profile.contactNumber && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PhoneOutlinedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                  <Typography sx={{ fontSize: 13, opacity: 0.95 }}>{profile.contactNumber}</Typography>
                </Stack>
              )}
              {profile.dateOfJoining && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                  <Typography sx={{ fontSize: 13, opacity: 0.95 }}>
                    Joined {new Date(profile.dateOfJoining).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Personal Information" accent="#6C50BF">
            <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Employee ID" value={profile.employeeid} />
            <InfoRow icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={profile.email} />
            <InfoRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Contact Number" value={profile.contactNumber} />
            <InfoRow icon={<CalendarMonthOutlinedIcon fontSize="small" />} label="Date of Birth" value={profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString("en-IN") : null} />
            <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="PAN" value={profile.panNumber} />
            <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Aadhar" value={profile.aadharNumber} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Employment" accent="#39C645">
            <InfoRow icon={<WorkOutlineOutlinedIcon fontSize="small" />} label="Role" value={profile.role} />
            <InfoRow icon={<WorkOutlineOutlinedIcon fontSize="small" />} label="Designation" value={profile.designation} />
            <InfoRow icon={<WorkOutlineOutlinedIcon fontSize="small" />} label="Department" value={profile.department} />
            <InfoRow icon={<PersonOutlineOutlinedIcon fontSize="small" />} label="Reporting Manager" value={profile.managerName ? `${profile.managerName} (${profile.manager})` : profile.manager} />
            <InfoRow icon={<LocationOnOutlinedIcon fontSize="small" />} label="Job Location" value={profile.jobLocation} />
            <InfoRow icon={<WorkOutlineOutlinedIcon fontSize="small" />} label="Work Mode" value={profile.workMode} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Compensation" accent="#F9BC06">
            <InfoRow
              icon={<CurrencyRupeeOutlinedIcon fontSize="small" />}
              label="Annual CTC"
              value={profile.offeredSalary ? `₹${Number(profile.offeredSalary).toLocaleString("en-IN")}` : null}
            />
            <InfoRow
              icon={<CurrencyRupeeOutlinedIcon fontSize="small" />}
              label="TDS (Monthly)"
              value={profile.TDSAmount ? `₹${Number(profile.TDSAmount).toLocaleString("en-IN")}` : null}
            />
            <InfoRow icon={<CurrencyRupeeOutlinedIcon fontSize="small" />} label="Payment Mode" value={profile.paymentMode || "Bank Transfer"} />
            <InfoRow icon={<CurrencyRupeeOutlinedIcon fontSize="small" />} label="Bank Name" value={profile.bankName} />
            <InfoRow icon={<CurrencyRupeeOutlinedIcon fontSize="small" />} label="Account Number" value={profile.accountNumber} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Leave Balance" subtitle="Days remaining in current cycle" accent="#5C85FF">
            <Stack spacing={2.5}>
              <LeaveBar
                label="Annual Leave"
                used={(leaveTotals.annualLeave - (leaves.annualLeave ?? leaveTotals.annualLeave))}
                total={leaveTotals.annualLeave}
                color="#6C50BF"
              />
              <LeaveBar
                label="Sick Leave"
                used={(leaveTotals.sickLeave - (leaves.sickLeave ?? leaveTotals.sickLeave))}
                total={leaveTotals.sickLeave}
                color="#F9BC06"
              />
              <LeaveBar
                label="Casual Leave"
                used={(leaveTotals.casualleave - (leaves.casualleave ?? leaveTotals.casualleave))}
                total={leaveTotals.casualleave}
                color="#39C645"
              />
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Documents"
            subtitle="Personal documents on file"
            accent="#FF6B6B"
            actions={[
              <Button key="upload" size="small" startIcon={<FileUploadOutlinedIcon />} variant="outlined" href="/my-documents">
                Upload new
              </Button>,
            ]}
          >
            {profile.documents && Object.keys(profile.documents).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(profile.documents).map(([key, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
                        display: "flex", alignItems: "center", gap: 1.5,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                      }}
                    >
                      <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: "#F4EDFA", color: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <InsertDriveFileOutlinedIcon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </Typography>
                        {value ? (
                          <Typography component="a" href={value} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 11, color: "primary.main", textDecoration: "none", fontWeight: 600 }}>
                            View document
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Not uploaded</Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary" textAlign="center" py={3}>
                No documents uploaded yet.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
